<script>
import gql from 'graphql-tag';
import { isObject, debounce } from '@/helpers/utils';

export default {
	data() {
		return {
			facility: this.getInitialFacility(),
			simulationType: this.getInitialSimulationType(),
			simulationClass: -1,
			school: '',
			educator: '',
			schoolInput: '',
			educatorInput: '',
			educators: [],
			schools: [],
			simulationClasses: [],
			minLettersInput: 3,
			debounceTime: 300,
			debounceSearchSchools: null,
			debounceSearchEducators: null
		};
	},
	computed: {
		selectedFacility() {
			return this.$store.getters.selectedFacility;
		},
		selectedFacilityId() {
			return this.selectedFacility ? this.selectedFacility.facility_ID : null;
		}
	},
	watch: {
		requiredFieldsPopulated(newValue, oldValue) {
			if (newValue != oldValue) {
				this.$emit('toggleButton', newValue);
			}
		},
		schoolInput(value) {
			if (value && value.length >= this.minLettersInput) {
				this.debounceSearchSchools(value);
			} else {
				this.school = '';
				this.schools = [];
			}
		},
		educatorInput(value) {
			if (value && value.length >= this.minLettersInput) {
				this.debounceSearchEducators(value);
			} else {
				this.educator = '';
				this.educators = [];
			}
		}
	},
	created() {
		this.debounceSearchSchools = debounce(value => {
			this.school = value;
			this.getSchools();
		}, this.debounceTime);

		this.debounceSearchEducators = debounce(value => {
			this.educator = value;
			this.getEducators();
		}, this.debounceTime);
	},
	mounted() {
		if (this.filters) {
			if (this.filters.school) {
				const selectedSchool = isObject(this.filters.school)
					? this.filters.school.value
					: this.filters.school;
				this.school = selectedSchool;
				this.schoolInput = selectedSchool;
			}
			if (this.filters.educator) {
				const selectedEducator = isObject(this.filters.educator)
					? this.filters.educator.value
					: this.filters.educator;
				this.educator = selectedEducator;
				this.educatorInput = selectedEducator;
			}

			if (this.filters.simulationClass) {
				this.getSimulationClasses().then(() => {
					const selectedClass = isObject(this.filters.simulationClass)
						? this.filters.simulationClass.value
						: this.filters.simulationClass;
					this.simulationClass = this.simulationClasses.findIndex(
						simulationClass => simulationClass.value == selectedClass
					);
				});
			}

			this.$emit('toggleButton', true);
		}
	},
	methods: {
		getInitialFacility() {
			if (this.selectedFacilityId != null) {
				return this.selectedFacilityId;
			}
			return -1;
		},
		getInitialSimulationType() {
			if (this.filters && this.filters.simulationType) {
				const selectedSimulationType = isObject(this.filters.simulationType)
					? this.filters.simulationType.value
					: this.filters.simulationType;
				return this.simulationTypes.findIndex(
					simulationType => simulationType.value == selectedSimulationType
				);
			}
			return -1;
		},
		onSchoolSelected(school) {
			this.school = school;
			this.schools = [];
		},
		onEducatorSelected(educator) {
			this.educator = educator;
			this.educators = [];
		},
		async getSchools() {
			if (this.schoolInput.length >= this.minLettersInput) {
				const filters = [];
				if (
					this.facility >= 0 &&
					this.facilities[this.facility].value != 'all'
				) {
					filters.push(`facility_ID=${this.facilities[this.facility].value}`);
				}

				filters.push(`(school.Contains("${this.schoolInput}"))`);

				const response = await this.$apollo.query({
					query: gql`
						query Schools($where: String!) {
							simulations(uniquefield: "school", where: $where) {
								school
							}
						}
					`,

					variables: {
						where: filters.join('&&')
					},
					fetchPolicy: 'no-cache'
				});

				if (response.data && response.data.simulations) {
					this.schools = [];
					response.data.simulations.forEach(sim => {
						this.schools.push(sim.school);
					});
				}
			}
		},
		async getEducators() {
			if (this.educatorInput.length >= this.minLettersInput) {
				const filters = [];
				const names = this.educatorInput.split(' ');

				if (names.length == 1) {
					filters.push(
						`((firstName.Contains("${this.educatorInput}")) || (lastName.Contains("${this.educatorInput}")))`
					);
				} else {
					filters.push(
						`((firstName.Contains("${names[0]}")) && (lastName.Contains("${names[1]}")))`
					);
				}

				filters.push('roleId="5"');

				const response = await this.$apollo.query({
					query: gql`
						query Educators($where: String!) {
							users(uniquefield: "new {firstName,lastName}", where: $where) {
								id
								firstName
								lastName
							}
						}
					`,

					variables: {
						where: filters.join('&&')
					},
					fetchPolicy: 'no-cache'
				});

				if (response.data && response.data.users) {
					this.educators = [];
					response.data.users.forEach(user => {
						this.educators.push(`${user.firstName} ${user.lastName}`);
					});
				}
			}
		},
		async getSimulationClasses() {
			const filters = [];
			if (this.facility >= 0 && this.facilities[this.facility].value != 'all') {
				filters.push(`facility_ID = ${this.facilities[this.facility].value}`);
			}
			if (this.school) {
				filters.push(`school.Contains("${this.school}")`);
			}

			if (this.educator) {
				const educatorNames = this.educator.split(' ');

				if (educatorNames.length == 1) {
					filters.push(
						`UserSimulations.Count(it.User.firstName.Contains("${this.educator}") || it.User.lastName.Contains("${this.educator}")) > 0`
					);
				} else {
					filters.push(
						`UserSimulations.Count(it.User.firstName.Contains("${educatorNames[0]}") && it.User.lastName.Contains("${educatorNames[1]}")) > 0`
					);
				}
			}
			const response = await this.$apollo.query({
				query: gql`
					query SimulationClasses($where: String!) {
						simulations(uniquefield: "sessionName", where: $where) {
							sessionName
						}
					}
				`,

				variables: {
					where: filters.join('&&')
				},
				fetchPolicy: 'no-cache'
			});

			if (response.data && response.data.simulations) {
				this.simulationClasses = [];
				response.data.simulations.forEach(sim => {
					this.simulationClasses.push({
						name: sim.sessionName,
						value: sim.sessionName
					});
				});
			}
		}
	}
};
</script>
